import { AuthResponse, ForecastResponse, ForecastRegion, UserAccountResponse, PointWindgramResult, SessionCheck } from './types';
import { storageService } from '../services/StorageService';

const BASE_URL = 'https://www.cumulus.it/php';
const REQUEST_ORIGIN = 'https://rasp.balanci.ng'

class ApiClient {
  private token: string | null = null;

  constructor() {
    this.token = storageService.getAuthToken();
  }


  async authenticate(userKey: string): Promise<AuthResponse> {
    try {
      const url = `${BASE_URL}/rasp_mobile.php?key=${encodeURIComponent(userKey)}&req=reg`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': '*/*',
          'Origin': REQUEST_ORIGIN,
        },
      });

      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}: ${response.statusText}` };
      }

      const text = await response.text();

      if (text.trim() === 'OK') {
        this.token = userKey;
        storageService.setAuthToken(userKey);
        return { success: true, token: userKey };
      } else {
        return { success: false, error: 'Invalid authentication key' };
      }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Network error' 
      };
    }
  }

  async getForecastLocations(onUpdate: (data: ForecastRegion[]) => void): Promise<ForecastResponse> {
    // Load cache immediately if it exists (for fast initial render)
    const cachedData = storageService.getCachedForecast();

    if (cachedData) {
      console.log('Returning cached forecast data immediately');

      // Start background fetch to revalidate
      this.revalidateForecastCache(cachedData, onUpdate);

      return { success: true, data: cachedData };
    }

    // No cache - fetch from server and wait
    return this.fetchFreshForecastLocations();
  }

  private async revalidateForecastCache(cachedData:  ForecastRegion[], onUpdate: ((data: ForecastRegion[]) => void)): Promise<void> {
    const forecastLocationsResponse = await this.fetchForecastLocations();
    const {success, data, error} = forecastLocationsResponse;

    if (!success || !data) {
      console.log(`Forecast data revalidation failed: ${error}`);
      return;
    }

    // Compare with cache - only update if data has changed
    const dataChanged = JSON.stringify(cachedData) !== JSON.stringify(data);
    if (dataChanged) {
      console.log('Forecast data changed during revalidation, updating cache');
      storageService.setCachedForecast(data);
      onUpdate(data);
    } else {
      console.log('Forecast data unchanged during revalidation');
    }
  }

  private async fetchFreshForecastLocations(): Promise<ForecastResponse> {
    const forecastLocationsResponse = await this.fetchForecastLocations();
    const {success, data} = forecastLocationsResponse;

    if (success && data) {
      console.log('Caching fresh forecast data');
      storageService.setCachedForecast(data);
    }

    return forecastLocationsResponse;
  }

  private async fetchForecastLocations(): Promise<ForecastResponse> {
    try {
      const url = `${BASE_URL}/ajax.new.php?type=v-windgrams`;
      const headers: Record<string, string> = {
        'Accept': '*/*',
        'Origin': REQUEST_ORIGIN,
      };

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}: ${response.statusText}` };
      }

      const freshData: ForecastRegion[] = await response.json();

      return { success: true, data: freshData };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  isAuthenticated(): boolean {
    return this.token !== null;
  }

  logout(): void {
    this.token = null;
    storageService.clearAuthToken();
  }

  clearAuthOnError(): void {
    this.logout();
  }

  // Get windgram image URL
  // Browser handles all caching via HTTP cache headers (Cache-Control, Expires, etc.)
  // when this url is used with an <img> tag.
  getWindgramUrl(windgramId: string, day: number = 0): Error | String {
    if (!this.token) {
      return { name: 'Not authenticated', message: 'Must be logged in' };
    }

    if (day < 0 || day > 4) {
      return { name: 'Invalid day', message: 'Day must be between 0 and 4' };
    }

    const params = new URLSearchParams({
      type: 'WINDGRAMS',
      key: this.token,
      secret: windgramId,
      day: day.toString()
    });

    return `https://www.cumulus.it/rasp/publicwg.php?${params.toString()}`;
  }

  /**
   * Windgram di un punto qualunque (pressione lunga sulla mappa, o le coordinate di un sito).
   * Ritaglio e cella li sceglie il server; 404 = non disponibile li' (nessun addebito).
   * La cache HTTP (private, 30 min) evita di richiederlo cambiando giorno avanti e indietro;
   * riaprirlo non costa comunque, l'addebito e' uno per (utente, giorno, cella).
   */
  /**
   * Sessione attiva (una da computer e una da smartphone per conto, insieme a viewer FIVL e
   * sito: cumulus-projects/php/rasp_sessione.php). L'installazione si riconosce dalla chiave.
   * null se il server non risponde: in quel caso non si ferma niente.
   */
  async checkSession(take: boolean = false): Promise<SessionCheck | null> {
    if (!this.token) return null;
    try {
      const url = `https://www.cumulus.it/rasp/app_sessione.php?key=${encodeURIComponent(this.token)}` +
        (take ? '&prendi=1' : '') + `&_=${Date.now()}`;
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) return null;
      const body = await response.json();
      return body && typeof body.ok === 'boolean' ? body as SessionCheck : null;
    } catch (e) {
      return null;
    }
  }

  async getPointWindgram(lat: number, lon: number, day: number): Promise<PointWindgramResult> {
    if (!this.token) {
      return { ok: false, status: 401, error: 'Not authenticated' };
    }
    const params = new URLSearchParams({
      key: this.token,
      lat: lat.toFixed(5),
      lon: lon.toFixed(5),
      day: day.toString(),
    });
    try {
      const response = await fetch(`https://www.cumulus.it/rasp/app_windgram.php?${params.toString()}`);
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        return { ok: false, status: response.status, error: (body && body.errore) || `HTTP ${response.status}` };
      }
      if (!body || !body.colonna) {
        return { ok: false, status: 404, error: 'no data' };
      }
      return { ok: true, data: body };
    } catch (error) {
      return { ok: false, status: 0, error: error instanceof Error ? error.message : 'Network error' };
    }
  }

  /**
   * Riquadro account (tabella HTML dal server). Dalla 3.5.0 app_account.php: nome e cognome
   * per le utenze Google/Facebook, username senza dominio per le altre. Se non c'e' (su
   * cumulus una pagina mancante risponde 200 con la home), si ripiega su paytest-mobile.php.
   */
  async getUserAccount(lang: string = 'it'): Promise<UserAccountResponse> {
    if (!this.token) {
      return { success: false, error: 'Not authenticated' };
    }

    const q = `?lang=${encodeURIComponent(lang)}&key=${encodeURIComponent(this.token)}`;
    const get = async (file: string) => {
      const response = await fetch(`${BASE_URL}/${file}${q}`, {
        method: 'GET',
        headers: { 'Accept': '*/*', 'Origin': REQUEST_ORIGIN },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      return response.text();
    };

    try {
      let html = '';
      try {
        html = await get('app_account.php');
      } catch (e) { /* si prova il vecchio */ }
      if (!/^\s*<table/i.test(html)) html = await get('paytest-mobile.php');

      // Extract username from HTML for admin checks
      const username = this.extractUsernameFromHtml(html);

      return { success: true, rawHtml: html, username };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  private extractUsernameFromHtml(html: string): string | undefined {
    try {
      // The HTML contains a table with "Username:" label
      // Look for the pattern: Username:</td><td>actual_username</td>
      const usernameMatch = html.match(/Username:<\/td>\s*<td[^>]*>(.*?)<\/td>/i);
      if (usernameMatch && usernameMatch[1]) {
        return usernameMatch[1].trim();
      }
      return undefined;
    } catch (error) {
      console.warn('Failed to extract username from HTML:', error);
      return undefined;
    }
  }

  getAuthToken(): string | null {
    return this.token;
  }
}

export const apiClient = new ApiClient();